import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Warning, WarningCircle, Info, MagnifyingGlass, ListMagnifyingGlass, ArrowClockwise, Trash,
} from "@phosphor-icons/react";

const REFRESH_MS = 10000;
const nn = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const CATEGORIES = [
  { k: "", label: "All categories" },
  { k: "routing", label: "Routing" },
  { k: "engine", label: "AI Engine" },
  { k: "flow", label: "Campaign Flow" },
  { k: "capacity", label: "Capacity" },
  { k: "media", label: "Media" },
  { k: "opensips", label: "OpenSIPS" },
];

const WINDOWS = [
  { k: 1, label: "Last 1h" },
  { k: 24, label: "Last 24h" },
  { k: 168, label: "Last 7d" },
  { k: 720, label: "Last 30d" },
];

const fmtTs = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

const LevelBadge = ({ level }) => {
  const map = {
    error: { cls: "bg-destructive/10 text-destructive", Icon: WarningCircle },
    warning: { cls: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]", Icon: Warning },
    info: { cls: "bg-primary/10 text-primary", Icon: Info },
  };
  const { cls, Icon } = map[level] || map.info;
  return (
    <span
      data-testid={`syslog-level-${level}`}
      className={"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold capitalize " + cls}
    >
      <Icon className="w-3.5 h-3.5" weight="bold" />
      {level}
    </span>
  );
};

const SummaryCard = ({ label, value, tone, active, onClick, testid }) => {
  const tones = {
    error: "text-destructive bg-destructive/10 border-destructive/20 ring-destructive/30",
    warning: "text-[hsl(var(--warning))] bg-[hsl(var(--warning)/0.12)] border-[hsl(var(--warning)/0.25)] ring-[hsl(var(--warning)/0.3)]",
    info: "text-primary bg-primary/10 border-primary/20 ring-primary/30",
  };
  const t = tones[tone] || tones.info;
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className={
        "flex items-center justify-between rounded-xl border bg-card card-shadow p-5 text-left transition-all duration-200 hover:-translate-y-0.5 " +
        (active ? `ring-2 ${t.split(" ").slice(-1)[0]} border-current` : "border-border")
      }
    >
      <span>
        <span className="block text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
        <span className="block text-3xl font-bold tabular-nums mt-2">{value}</span>
      </span>
      <span className={"flex h-11 w-11 items-center justify-center rounded-lg " + t.split(" ").slice(0, 2).join(" ")}>
        {tone === "error" ? <WarningCircle className="w-6 h-6" weight="duotone" />
          : tone === "warning" ? <Warning className="w-6 h-6" weight="duotone" />
          : <Info className="w-6 h-6" weight="duotone" />}
      </span>
    </button>
  );
};

export default function SystemLogs() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ by_level: {}, top_reasons: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("");      // "" | error | warning | info
  const [category, setCategory] = useState("");
  const [hours, setHours] = useState(24);
  const [clearing, setClearing] = useState(false);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const params = { hours, limit: 200 };
      if (level) params.level = level;
      if (category) params.category = category;
      if (q.trim()) params.q = q.trim();
      const [logs, sum] = await Promise.all([
        api.get("/system-logs", { params }),
        api.get("/system-logs/summary", { params: { hours } }),
      ]);
      setRows(logs.data.items || []);
      setSummary(sum.data || { by_level: {}, top_reasons: [], total: 0 });
    } catch {
      /* transient polling error — keep last snapshot */
    } finally {
      if (firstLoad.current) { setLoading(false); firstLoad.current = false; }
    }
  }, [hours, level, category, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const toggleLevel = (l) => setLevel((cur) => (cur === l ? "" : l));

  const clearLogs = useCallback(async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/system-logs");
      toast.success(`Cleared ${data.deleted} log ${data.deleted === 1 ? "entry" : "entries"}`);
      await load();
    } catch {
      toast.error("Could not clear logs. Please try again.");
    } finally {
      setClearing(false);
    }
  }, [load]);

  return (
    <div data-testid="system-logs-page">
      <PageHeader
        title="System Logs"
        subtitle="Reasons calls fail to connect — routing, AI engine, campaign flow and capacity events. Updates automatically."
      >
        <button
          type="button"
          onClick={load}
          data-testid="syslog-refresh"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowClockwise className="w-4 h-4" weight="bold" />
          Refresh
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              data-testid="syslog-clear-btn"
              disabled={clearing}
              className="inline-flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/15 transition-colors disabled:opacity-50"
            >
              <Trash className="w-4 h-4" weight="bold" />
              Clear logs
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="syslog-clear-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Clear all system logs?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes every recorded system log entry. New events
                will continue to be captured as calls come in. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="syslog-clear-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="syslog-clear-confirm"
                onClick={clearLogs}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete all logs
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <SummaryCard testid="syslog-card-error" label="Errors (dropped calls)"
          value={summary.by_level?.error ?? 0} tone="error"
          active={level === "error"} onClick={() => toggleLevel("error")} />
        <SummaryCard testid="syslog-card-warning" label="Warnings"
          value={summary.by_level?.warning ?? 0} tone="warning"
          active={level === "warning"} onClick={() => toggleLevel("warning")} />
        <SummaryCard testid="syslog-card-info" label="Info"
          value={summary.by_level?.info ?? 0} tone="info"
          active={level === "info"} onClick={() => toggleLevel("info")} />
      </div>

      {summary.top_reasons?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="syslog-top-reasons">
          <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground mr-1">Top reasons</span>
          {summary.top_reasons.map((r) => (
            <button
              key={r.reason}
              type="button"
              data-testid={`syslog-reason-chip-${r.reason}`}
              onClick={() => setQ(r.reason)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium hover:bg-muted transition-colors"
            >
              <span className="font-mono">{r.reason}</span>
              <span className="rounded-full bg-background px-1.5 tabular-nums text-muted-foreground">{r.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="syslog-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reason, message, prefix, source IP or call UUID"
            className="pl-9 rounded-lg"
          />
        </div>
        <select
          data-testid="syslog-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
        >
          {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
        </select>
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1" data-testid="syslog-window">
          {WINDOWS.map((w) => (
            <button
              key={w.k}
              type="button"
              data-testid={`syslog-window-${w.k}`}
              aria-pressed={hours === w.k}
              onClick={() => setHours(w.k)}
              className={
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 " +
                (hours === w.k ? "bg-card text-foreground card-shadow" : "text-muted-foreground hover:text-foreground")
              }
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading system logs…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ListMagnifyingGlass}
          title="No system events in this window"
          description="When a call fails to route — an unknown VICIdial source, an unmatched prefix, an inactive campaign, no engine capacity or no runnable flow — the reason will appear here."
          testid="system-logs-empty"
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="system-logs-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Source IP</TableHead>
                <TableHead>Call UUID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`syslog-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground" data-testid="syslog-ts">{fmtTs(r.ts)}</TableCell>
                  <TableCell><LevelBadge level={r.level} /></TableCell>
                  <TableCell className="capitalize" data-testid="syslog-category-cell">{nn(r.category)}</TableCell>
                  <TableCell className="font-mono text-xs" data-testid="syslog-reason">{nn(r.reason)}</TableCell>
                  <TableCell className="max-w-md text-sm" data-testid="syslog-message">{nn(r.message)}</TableCell>
                  <TableCell className="tabular-nums" data-testid="syslog-prefix">{nn(r.campaign_prefix)}</TableCell>
                  <TableCell className="tabular-nums" data-testid="syslog-source-ip">{nn(r.source_ip)}</TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground truncate max-w-[10rem]" title={r.call_uuid || ""} data-testid="syslog-call-uuid">{nn(r.call_uuid)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
